import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbSpinnerModule,
  NbToggleModule,
} from '@nebular/theme';
import { TranslateModule } from '@ngx-translate/core';
import { TextFieldModule } from '@angular/cdk/text-field';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { KPI } from '../../../../../@core/api';
import { ScopeService } from '../../../../../@core/utils';
import { idMapToArray, toLoadingState } from '../../../../../@core/utils/scope.service';

@Component({
  selector: 'app-taxonomy-selector',
  template: `
    <nb-card style="width: 45vw; height: 100%;">
      <nb-card-body class="investment-body">
        <form [formGroup]="taxonomyForm">
          <div class="form-content">
            <!-- Toggle for Taxonomy Alignment -->
            <div class="form-field toggle-field">
              <label>{{ 'Potentially aligned with EU taxonomy' | translate }}</label>
              <nb-toggle formControlName="enabled"></nb-toggle>
            </div>

            @if (taxonomyForm.get('enabled')?.value) {
              <!-- Reason (Taxonomy Objective) -->
              <div class="form-field" [nbSpinner]="(taxonomyObjectives$ | async)?.loading">
                <label for="taxonomy_objective">{{ 'Reason' | translate }}</label>
                <nb-select
                  id="taxonomy_objective"
                  formControlName="taxonomy_objective"
                  fullWidth
                  placeholder="{{ 'Select a reason' | translate }}"
                >
                  @for (obj of (taxonomyObjectives$ | async)?.data; track obj.id) {
                    <nb-option [value]="obj">
                      {{ obj.name }}
                    </nb-option>
                  }
                </nb-select>
              </div>

              <!-- Aligned Activity Category - Only show when objective has associated categories -->
              @if (taxonomyForm.get('taxonomy_objective')?.value && (filteredCategories$ | async)?.length) {
                <div class="form-field" [nbSpinner]="(taxonomyCategories$ | async)?.loading">
                  <label for="taxonomy_category">{{ 'Aligned activity category' | translate }}</label>
                  <nb-select
                    id="taxonomy_category"
                    formControlName="taxonomy_category"
                    fullWidth
                    placeholder="{{ 'Select a category' | translate }}"
                  >
                    @for (cat of filteredCategories$ | async; track cat.id) {
                      <nb-option [value]="cat">
                        {{ cat.activity_number }} {{ cat.name }}
                      </nb-option>
                    }
                  </nb-select>
                </div>
              }

              <!-- Taxonomy Comment - Show after Category is selected, or after Objective if no categories exist -->
              @if (taxonomyForm.get('taxonomy_category')?.value || (taxonomyForm.get('taxonomy_objective')?.value && !(filteredCategories$ | async)?.length)) {
                <div class="form-field">
                  <label for="taxonomy_comment">{{ 'Taxonomy comment' | translate }}</label>
                  <textarea
                    nbInput
                    id="taxonomy_comment"
                    formControlName="taxonomy_comment"
                    fullWidth
                    cdkTextareaAutosize
                    cdkAutosizeMinRows="3"
                    placeholder=""
                    readonly
                  ></textarea>
                </div>
              }
            }
          </div>
        </form>
      </nb-card-body>
      @if (isDirty && (!taxonomyForm.get('enabled')?.value || (taxonomyForm.get('taxonomy_objective')?.value && (!(filteredCategories$ | async)?.length || taxonomyForm.get('taxonomy_category')?.value)))) {
        <nb-card-footer class="investment-footer">
          <button nbButton class="investment-next" (click)="onSave()">
            {{ 'Save' | translate }}
          </button>
        </nb-card-footer>
      }
    </nb-card>
  `,
  styleUrls: ['./taxonomy-selector.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSelectModule,
    NbOptionModule,
    NbButtonModule,
    NbIconModule,
    NbSpinnerModule,
    NbToggleModule,
    NbInputModule,
    TextFieldModule,
    TranslateModule,
  ],
})
export class TaxonomySelectorComponent implements OnInit, OnDestroy {
  @Input() investment!: KPI.MappedInvestment;
  @Input() readOnly: boolean = false;
  @Output() save = new EventEmitter<{
    taxonomy_objective?: any;
    taxonomy_category?: any;
    taxonomy_comment?: string;
  }>();

  private scopeService = inject(ScopeService);

  public taxonomyForm = new FormGroup({
    enabled: new FormControl<boolean>(false),
    taxonomy_objective: new FormControl<any>(null),
    taxonomy_category: new FormControl<any>(null),
    taxonomy_comment: new FormControl<string>(''),
  });

  public taxonomyObjectives$ = this.scopeService.taxonomyObjectives$.pipe(
    idMapToArray(),
    toLoadingState<KPI.TaxonomyObjective[]>(),
  );

  public taxonomyCategories$ = this.scopeService.taxonomyCategories$.pipe(
    idMapToArray(),
    toLoadingState<KPI.TaxonomyCategory[]>(),
  );

  public filteredCategories$ = new BehaviorSubject<KPI.TaxonomyCategory[]>([]);
  public isDirty = false;

  private initialValues: any;
  private formChangesSub?: Subscription;

  ngOnInit() {
    const isEnabled = !!(this.investment.taxonomy_objective || this.investment.taxonomy_category);

    combineLatest([this.taxonomyObjectives$, this.taxonomyCategories$]).subscribe(
      ([objectivesState, categoriesState]) => {
        let taxonomyObjective = null;
        let taxonomyCategory = null;

        if (this.investment.taxonomy_objective && objectivesState.data) {
          taxonomyObjective = objectivesState.data.find(
            obj => obj.id === this.investment.taxonomy_objective?.id
          );
        }

        if (this.investment.taxonomy_category && categoriesState.data) {
          taxonomyCategory = categoriesState.data.find(
            cat => cat.id === this.investment.taxonomy_category?.id
          );
        }

        const investmentAny = this.investment as any;

        this.initialValues = {
          enabled: isEnabled,
          taxonomy_objective: taxonomyObjective,
          taxonomy_category: taxonomyCategory,
          taxonomy_comment: investmentAny.taxonomy_comment || '',
        };

        this.taxonomyForm.patchValue(this.initialValues, { emitEvent: false });
        this.isDirty = false;

        if (taxonomyObjective && categoriesState.data) {
          this.updateFilteredCategories(taxonomyObjective.id, categoriesState.data);
        }
      }
    );

    if (this.readOnly) {
      this.taxonomyForm.disable();
    }

    // Listen to toggle changes
    this.taxonomyForm.get('enabled')?.valueChanges.subscribe(enabled => {
      if (!enabled) {
        this.taxonomyForm.patchValue({
          taxonomy_objective: null,
          taxonomy_category: null,
          taxonomy_comment: '',
        }, { emitEvent: false });
        this.filteredCategories$.next([]);
      }
    });

    // Listen to objective changes to filter categories
    this.taxonomyForm.get('taxonomy_objective')?.valueChanges.subscribe(objective => {
      if (objective) {
        this.taxonomyForm.patchValue({ taxonomy_category: null }, { emitEvent: false });

        this.taxonomyCategories$.subscribe(categoriesState => {
          if (categoriesState.data) {
            this.updateFilteredCategories(objective.id, categoriesState.data);
          }
        });
      } else {
        this.filteredCategories$.next([]);
      }
    });

    // Track dirty state on any form change
    this.formChangesSub = this.taxonomyForm.valueChanges.subscribe(() => {
      this.isDirty = this.hasChanges();
    });
  }

  ngOnDestroy() {
    this.formChangesSub?.unsubscribe();
  }

  private updateFilteredCategories(objectiveId: number, allCategories: KPI.TaxonomyCategory[]) {
    const filtered = allCategories.filter(cat => cat.objective?.id === objectiveId);
    this.filteredCategories$.next(filtered);
  }

  hasChanges(): boolean {
    const currentValues = this.taxonomyForm.value;
    return (
      currentValues.enabled !== this.initialValues?.enabled ||
      currentValues.taxonomy_objective?.id !== this.initialValues?.taxonomy_objective?.id ||
      currentValues.taxonomy_category?.id !== this.initialValues?.taxonomy_category?.id ||
      currentValues.taxonomy_comment !== this.initialValues?.taxonomy_comment
    );
  }

  onSave() {
    const formValue = this.taxonomyForm.value;

    if (!formValue.enabled) {
      this.save.emit({
        taxonomy_objective: null,
        taxonomy_category: null,
        taxonomy_comment: '',
      });
    } else {
      this.save.emit({
        taxonomy_objective: formValue.taxonomy_objective || null,
        taxonomy_category: formValue.taxonomy_category || null,
        taxonomy_comment: formValue.taxonomy_comment || '',
      });
    }

    this.isDirty = false;
  }
}
